import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  Container, 
  Grid, 
  Typography, 
  Box, 
  Card, 
  CardContent, 
  TextField, 
  MenuItem, 
  FormControl, 
  InputLabel, 
  Select, 
  SelectChangeEvent,
  IconButton,
  Button,
  ToggleButton,
  ToggleButtonGroup,
  InputAdornment,
  Chip,
  Avatar,
  Paper,
  Divider,
  Stack
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import GridViewIcon from '@mui/icons-material/GridView';
import ViewListIcon from '@mui/icons-material/ViewList';
import SortIcon from '@mui/icons-material/Sort';
import { Team, League, News } from '../types';
import { ApiErrorResponse } from '../types/errors';
import teamService from '../services/teamService';
import leagueService from '../services/leagueService';
import newsService from '../services/newsService';
import { useApiError } from '../hooks/useApiError';
import LoadingDisplay from '../components/common/LoadingDisplay';
import ErrorDisplay from '../components/common/ErrorDisplay';

// Map browser languages to league names
const languageToLeagueMap: { [key: string]: string } = {
  'tr': 'Süper Lig',
  'en': 'Premier League',
  'es': 'La Liga',
  'de': 'Bundesliga',
  'it': 'Serie A',
  'fr': 'Ligue 1',
  'pt': 'Primeira Liga',
  'nl': 'Eredivisie',
  'ru': 'Premier League',
  'ar': 'Saudi Pro League',
  'ja': 'J1 League',
  'ko': 'K League 1',
  'zh': 'Chinese Super League'
};

const TeamsPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { handleError } = useApiError();
  const initialFetchDone = useRef(false);
  
  // State for data
  const [teams, setTeams] = useState<Team[]>([]);
  const [leagues, setLeagues] = useState<League[]>([]);
  const [selectedTeam, setSelectedTeam] = useState<Team | null>(null);
  const [teamNews, setTeamNews] = useState<News[]>([]);
  const teamNewsRef = useRef<HTMLDivElement>(null);
  const [leagueNews, setLeagueNews] = useState<News[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // State for filters
  const [selectedLeague, setSelectedLeague] = useState<number | "">('');
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [sortBy, setSortBy] = useState<'name' | 'league'>('name');

  // Function to get browser language
  const getBrowserLanguage = (): string => {
    const language = navigator.language || (navigator as any).userLanguage;
    return language.split('-')[0]; // Get primary language code
  };

  // Function to find league ID by name
  const findLeagueIdByName = (leagueName: string): number | undefined => {
    const league = leagues.find(l => l.name === leagueName);
    return league?.id;
  };

  // Fetch initial data and set default league
  useEffect(() => {
    if (initialFetchDone.current) return;
    initialFetchDone.current = true;

    const fetchInitialData = async () => {
      try {
        setLoading(true);
        // First fetch leagues
        const leaguesResponse = await leagueService.getAllLeagues();
        setLeagues(leaguesResponse.results);

        // Get browser language and find corresponding league
        const browserLang = getBrowserLanguage();
        const defaultLeagueName = languageToLeagueMap[browserLang] || 'Premier League';
        const defaultLeague = leaguesResponse.results.find(l => l.name === defaultLeagueName);
        
        if (defaultLeague) {
          setSelectedLeague(defaultLeague.id);
          // Fetch teams for the default league
          const teamsResponse = await teamService.getTeamsByLeague(defaultLeague.id);
          setTeams(teamsResponse);
        }
      } catch (err) {
        const apiError: ApiErrorResponse = {
          message: err instanceof Error ? err.message : 'Unknown error',
          code: 'UNKNOWN',
          originalError: err
        };
        handleError(apiError);
        setError(t('errors.fetchDataFailed'));
      } finally {
        setLoading(false);
      }
    };

    fetchInitialData();
  }, []); // Empty dependency array since we only want to fetch once on mount

  // Add effect to fetch teams when league changes
  useEffect(() => {
    const fetchTeamsByLeague = async () => {
      if (!selectedLeague) return;
      
      try {
        setLoading(true);
        const teamsResponse = await teamService.getTeamsByLeague(selectedLeague);
        setTeams(teamsResponse);
      } catch (err) {
        const apiError: ApiErrorResponse = {
          message: err instanceof Error ? err.message : 'Unknown error',
          code: 'UNKNOWN',
          originalError: err
        };
        handleError(apiError);
        setError(t('errors.fetchTeamsFailed'));
      } finally {
        setLoading(false);
      }
    };

    fetchTeamsByLeague();
  }, [selectedLeague, handleError, t]);

  // Fetch team news when a team is selected
  useEffect(() => {
    const fetchTeamNews = async () => {
      if (!selectedTeam) return;

      try {
        setLoading(true);
        const response = await newsService.getNewsByTeam(selectedTeam.id);
        setTeamNews(response);
      } catch (err) {
        const apiError: ApiErrorResponse = {
          message: err instanceof Error ? err.message : 'Unknown error',
          code: 'UNKNOWN',
          originalError: err
        };
        handleError(apiError);
        setError(t('errors.fetchNewsFailed'));
      } finally {
        setLoading(false);
      }
    };

    fetchTeamNews();
  }, [selectedTeam, handleError, t]);

  // Add effect to fetch league news when league changes
  useEffect(() => {
    const fetchLeagueNews = async () => {
      if (!selectedLeague) return;
      
      try {
        setLoading(true);
        const response = await newsService.getNewsByLeague(selectedLeague);
        setLeagueNews(response);
      } catch (err) {
        const apiError: ApiErrorResponse = {
          message: err instanceof Error ? err.message : 'Unknown error',
          code: 'UNKNOWN',
          originalError: err
        };
        handleError(apiError);
        setError(t('errors.fetchNewsFailed'));
      } finally {
        setLoading(false);
      }
    };

    fetchLeagueNews();
  }, [selectedLeague, handleError, t]);

  const handleTeamSelect = (team: Team) => {
    setSelectedTeam(team);
    setTeamNews([]);
    setLoading(true);
    setError(null);

    fetch(`https://api.transfersnews.org/api/v1/news/team/${team.id}`, {
      headers: {
        'Accept': 'application/json',
        'Accept-Language': i18n.language,
        'User-Agent': 'Mozilla/5.0 (Macintosh; Intel Mac OS X 10_15_7) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/91.0.4472.114 Safari/537.36'
      }
    })
      .then(response => {
        if (!response.ok) {
          throw new Error('Failed to fetch team news');
        }
        return response.json();
      })
      .then(data => {
        setTeamNews(data.data || []);
        // Scroll to team news section after data is loaded
        setTimeout(() => {
          teamNewsRef.current?.scrollIntoView({ behavior: 'smooth' });
        }, 100);
      })
      .catch(error => {
        console.error('Error fetching team news:', error);
        setError(error.message);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleLeagueChange = (event: SelectChangeEvent<number | "">) => {
    const newLeagueId = event.target.value === '' ? '' : Number(event.target.value);
    setSelectedLeague(newLeagueId);
    setSearchQuery('');
    setSortBy('name');
    setViewMode('grid');
    // Clear selected team and its news when league changes
    setSelectedTeam(null);
    setTeamNews([]);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
  };

  const handleViewModeChange = (event: React.MouseEvent<HTMLElement>, newViewMode: 'grid' | 'list') => {
    if (newViewMode !== null) {
      setViewMode(newViewMode);
    }
  };

  const handleSortChange = (event: SelectChangeEvent<'name' | 'league'>) => {
    setSortBy(event.target.value as 'name' | 'league');
  };

  const clearFilters = () => {
    setSelectedLeague('');
    setSearchQuery('');
  };

  const filteredTeams = teams
    .filter(team => {
      const matchesLeague = selectedLeague ? team.league_id === selectedLeague : true;
      const matchesSearch = searchQuery ? team.name.toLowerCase().includes(searchQuery.toLowerCase()) : true;
      return matchesLeague && matchesSearch;
    })
    .sort((a, b) => {
      if (sortBy === 'name') {
        return a.name.localeCompare(b.name);
      } else {
        const leagueA = leagues.find(l => l.id === a.league_id)?.name || '';
        const leagueB = leagues.find(l => l.id === b.league_id)?.name || '';
        return leagueA.localeCompare(leagueB);
      }
    });

  if (loading) {
    return <LoadingDisplay />;
  }

  if (error) {
    return <ErrorDisplay message={error} />;
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 1, mb: 1 }}>
      <Typography
        variant="h2"
        component="h1"
        gutterBottom
        align="center"
        sx={{
          fontFamily: '"Playfair Display", serif',
          mb: { xs: 2, sm: 3, md: 4 },
          borderBottom: '2px solid',
          borderColor: 'primary.main',
          pb: { xs: 1, sm: 2 },
        }}
      >
        {t('teams.title')}
      </Typography>

      <Grid container spacing={1}>
        {/* Filters Section */}
        <Grid item xs={12}>
          <Paper 
            elevation={3}
            sx={{ 
              p: 3,
              mb: 3,
              borderRadius: 2,
              background: 'linear-gradient(145deg, #ffffff 0%, #f5f5f5 100%)'
            }}
          >
            <Grid container spacing={1} alignItems="center">
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth>
                  <InputLabel>{t('teams.selectLeague')}</InputLabel>
                  <Select
                    value={selectedLeague}
                    label={t('teams.selectLeague')}
                    onChange={handleLeagueChange}
                  >
                    {leagues.map((league) => (
                      <MenuItem key={league.id} value={league.id}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Avatar
                            src={league.logo_url}
                            alt={league.name}
                            sx={{ width: 24, height: 24 }}
                          />
                          {league.name}
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  label={t('teams.searchPlaceholder')}
                  value={searchQuery}
                  onChange={handleSearchChange}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                    endAdornment: searchQuery && (
                      <InputAdornment position="end">
                        <IconButton onClick={() => setSearchQuery('')} edge="end">
                          <ClearIcon />
                        </IconButton>
                      </InputAdornment>
                    )
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <FormControl fullWidth>
                  <InputLabel>Sort By</InputLabel>
                  <Select
                    value={sortBy}
                    label="Sort By"
                    onChange={handleSortChange}
                    startAdornment={
                      <InputAdornment position="start">
                        <SortIcon />
                      </InputAdornment>
                    }
                  >
                    <MenuItem value="name">Name</MenuItem>
                    <MenuItem value="league">League</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <ToggleButtonGroup
                  value={viewMode}
                  exclusive
                  onChange={handleViewModeChange}
                  fullWidth
                >
                  <ToggleButton value="grid">
                    <GridViewIcon />
                  </ToggleButton>
                  <ToggleButton value="list">
                    <ViewListIcon />
                  </ToggleButton>
                </ToggleButtonGroup>
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <Button
                  fullWidth
                  variant="outlined"
                  onClick={clearFilters}
                  startIcon={<ClearIcon />}
                >
                  Clear Filters
                </Button>
              </Grid>
            </Grid>

            {/* Active Filters Display */}
            {(selectedLeague || searchQuery) && (
              <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {selectedLeague && (
                  <Chip
                    label={leagues.find(l => l.id === selectedLeague)?.name}
                    onDelete={() => setSelectedLeague('')}
                  />
                )}
                {searchQuery && (
                  <Chip
                    label={`Search: ${searchQuery}`}
                    onDelete={() => setSearchQuery('')}
                  />
                )}
              </Box>
            )}
          </Paper>
        </Grid>

        {/* Main Content Grid */}
        <Grid item xs={12} md={6}>
          {/* Teams Grid */}
          <Grid container spacing={1}>
            {filteredTeams.map((team) => (
              <Grid item xs={12} sm={6} key={team.id}>
                <Card
                  sx={{
                    cursor: 'pointer',
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: 4,
                      backgroundColor: 'action.hover'
                    },
                    '&:active': {
                      transform: 'translateY(-2px)',
                      boxShadow: 2
                    },
                    height: '100%',
                    display: 'flex',
                    flexDirection: viewMode === 'grid' ? 'column' : 'row',
                    alignItems: viewMode === 'grid' ? 'center' : 'center',
                    p: 2,
                    position: 'relative',
                    overflow: 'hidden',
                    '&::after': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      backgroundColor: 'rgba(0, 0, 0, 0.04)',
                      opacity: 0,
                      transition: 'opacity 0.2s ease-in-out'
                    },
                    '&:hover::after': {
                      opacity: 1
                    }
                  }}
                  onClick={() => handleTeamSelect(team)}
                >
                  <Box
                    sx={{
                      width: viewMode === 'grid' ? '100%' : 60,
                      height: viewMode === 'grid' ? 60 : 60,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mb: viewMode === 'grid' ? 2 : 0,
                      mr: viewMode === 'grid' ? 0 : 2
                    }}
                  >
                    <img
                      src={team.logo_url}
                      alt={team.name}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'contain'
                      }}
                    />
                  </Box>
                  <Box sx={{ flex: 1, textAlign: viewMode === 'grid' ? 'center' : 'left' }}>
                    <Typography variant="subtitle1" component="div" noWrap>
                      {team.name}
                    </Typography>
                    {viewMode === 'list' && (
                      <Typography variant="body2" color="text.secondary" noWrap>
                        {team.country}
                      </Typography>
                    )}
                  </Box>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Grid>

        {/* News Section */}
        <Grid item xs={12} md={6}>
          {selectedLeague && (
            <Card sx={{ mb: 1 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  {t('teams.transferRumors', { team: leagues.find(l => l.id === selectedLeague)?.name })}
                </Typography>
                <Grid container spacing={1}>
                  {leagueNews.map((news) => (
                    <Grid item xs={12} key={news.id}>
                      <Paper
                        elevation={2}
                        sx={{
                          p: 2,
                          cursor: 'pointer',
                          transition: 'all 0.2s ease-in-out',
                          '&:hover': {
                            transform: 'translateY(-4px)',
                            boxShadow: 4,
                            backgroundColor: 'action.hover'
                          },
                          '&:active': {
                            transform: 'translateY(-2px)',
                            boxShadow: 2
                          },
                          position: 'relative',
                          overflow: 'hidden',
                          '&::after': {
                            content: '""',
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            backgroundColor: 'rgba(0, 0, 0, 0.04)',
                            opacity: 0,
                            transition: 'opacity 0.2s ease-in-out'
                          },
                          '&:hover::after': {
                            opacity: 1
                          }
                        }}
                        onClick={() => window.open(news.url, '_blank')}
                      >
                        <Typography variant="subtitle1" gutterBottom>
                          {news.title}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          {new Date(news.created_at).toLocaleDateString()}
                        </Typography>
                        <Typography variant="body2" sx={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                          {news.content}
                        </Typography>
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
              </CardContent>
            </Card>
          )}
        </Grid>

        {/* Team News Section */}
        {selectedTeam && (
          <Grid item xs={12}>
            <Card sx={{ mt: 3 }} ref={teamNewsRef}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  {t('teams.newsFor', { team: selectedTeam.name })}
                </Typography>
                <Grid container spacing={1}>
                  {teamNews.map((news) => (
                    <Grid item xs={12} key={news.id}>
                      <Paper
                        elevation={2}
                        sx={{
                          p: 2,
                          cursor: 'pointer',
                          transition: 'all 0.2s ease-in-out',
                          '&:hover': {
                            transform: 'translateY(-4px)',
                            boxShadow: 4,
                            backgroundColor: 'action.hover'
                          },
                          '&:active': {
                            transform: 'translateY(-2px)',
                            boxShadow: 2
                          },
                          position: 'relative',
                          overflow: 'hidden',
                          '&::after': {
                            content: '""',
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            backgroundColor: 'rgba(0, 0, 0, 0.04)',
                            opacity: 0,
                            transition: 'opacity 0.2s ease-in-out'
                          },
                          '&:hover::after': {
                            opacity: 1
                          }
                        }}
                        onClick={() => window.open(news.url, '_blank')}
                      >
                        <Typography variant="subtitle1" gutterBottom>
                          {news.title}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          {new Date(news.created_at).toLocaleDateString()}
                        </Typography>
                        <Typography variant="body2" sx={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                          {news.content}
                        </Typography>
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>
    </Container>
  );
};

export default TeamsPage; 